import { useEffect } from "react";
import { useSelector } from "react-redux";
import { FaVideo } from "react-icons/fa";

import { useCalls } from "../CallsContext";
import {
  hasPeople,
  isIdle,
  isInRoom,
  isLive,
  participantFor,
  sameId,
  threadIdFor,
} from "../callsModel.mjs";

/** The one place every participant starts, joins, rejoins or returns to a thread's call. */
export default function CallHeaderActions({ thread }) {
  const calls = useCalls();
  const id = threadIdFor(thread);
  const activeId = useSelector((state) => state.calls?.activeByThread[id]);
  const snapshots = useSelector((state) => state.calls?.byId);
  const session = useSelector((state) => state.calls?.session);
  useEffect(() => {
    calls?.controller.rememberThread(thread);
  }, [calls?.controller, thread]);
  if (!calls || !id || thread.partner_unlinked) return null;
  const summary = thread.active_call;
  const active = snapshots[activeId] || (summary && snapshots[summary.id]) || summary;
  // An empty room is over even while the server finishes closing it.
  const live = isLive(active) && hasPeople(active);
  // Being in this thread's call (or preparing/ringing it) is never an invitation to join it.
  const current =
    !isIdle(session.phase) &&
    (sameId(session.threadId, id) || (live && sameId(session.callId, active.id)));
  if (current) {
    return (
      <div className="calls-header-actions">
        <button
          type="button"
          className="calls-header-button is-current"
          aria-label="You are in this call. Open the call window"
          title="Open the call window"
          onClick={() => calls.controller.patch({ minimized: false })}
        >
          <span className="calls-live-dot" aria-hidden="true" />
          <FaVideo aria-hidden="true" />
          <span className="calls-header-label">In call</span>
        </button>
      </div>
    );
  }
  const own = live && participantFor(active, calls.controller.getContext().userId)?.state;
  const label = own === "left" ? "Rejoin" : isInRoom(own) ? "Resume" : "Join";
  return (
    <div className="calls-header-actions">
      <button
        type="button"
        disabled={session.busy}
        className={`calls-header-button ${live ? "is-live" : ""}`}
        aria-label={live ? `${label} video call` : "Start video call"}
        title={live ? `${label} · ${active.in_room_count ?? 0} in call` : "Start video call"}
        onClick={() =>
          live ? calls.controller.resume(active.id) : calls.controller.openPreview(thread)
        }
      >
        {live && <span className="calls-live-dot" aria-hidden="true" />}
        <FaVideo aria-hidden="true" />
        {live && (
          <span className="calls-header-label">
            {label} · {active.in_room_count ?? 0}
          </span>
        )}
      </button>
    </div>
  );
}
